import { useState, useCallback, useEffect, useRef } from 'react'
import { AIQuestion, AIResponse, AIMessage } from '../types/ai'
import { useOwner } from '../contexts/OwnerContext'
import { config } from '../config'
import { v4 as uuidv4 } from 'uuid'

interface UseAIOptions {
  onSuccess?: (response: AIResponse) => void
  onError?: (error: string) => void
}

export const useAI = (options?: UseAIOptions) => {
  const { headers } = useOwner()
  const [messages, setMessages] = useState<AIMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track company to clear messages on company change
  const currentCompanyRef = useRef<string | null>(null)

  // Clear messages when company changes
  useEffect(() => {
    const newCompany = headers?.company || null
    if (currentCompanyRef.current !== null && currentCompanyRef.current !== newCompany) {
      // Company changed - clear messages
      setMessages([])
      setError(null)
    }
    currentCompanyRef.current = newCompany
  }, [headers?.company])

  const askQuestion = useCallback(
      async (
        question: string,
        screenContext?: string,
        startDate?: string | null,
        endDate?: string | null,
        skipCache: boolean = false
      ): Promise<AIResponse | null> => {
      try {
        setError(null)
        setIsLoading(true)

        // Validate we have authentication
        if (!headers?.userToken) {
          throw new Error('Token de autenticação não encontrado. Faça login novamente.')
        }

        // Adicionar pergunta do usuário aos messages
        const userMessage: AIMessage = {
          id: uuidv4(),
          type: 'user',
          content: question,
          timestamp: Date.now(),
        }
        setMessages((prev) => [...prev, userMessage])

        // Fazer requisição para o novo endpoint de question/create
        // O backend vai buscar dados, chamar GEMINI e salvar pergunta+resposta
        const response = await fetch(
          `${config.API_BASE_URL}/question/create`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${headers.userToken}`,
              'company': headers.company || '',
              'companyName': headers.companyName || '',
              'businessUnit': headers.businessUnit || '-1',
              'logisticOperator': headers.logisticOperator || '-1',
              'user': headers.user || '',
            },
            body: JSON.stringify({
              question,
              source: 'specialized', // Tela de perguntas especializadas
              screen_context: screenContext || undefined, // Contexto simples, backend vai enriquecer
              start_date: startDate || undefined,
              end_date: endDate || undefined,
              skip_cache: skipCache,
            }),
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Erro ao criar pergunta')
        }

        const data = (await response.json()) as {
          question_id: number
          question: string
          source: string
          answer: string | null
          created_at: string
          usage_alert?: string | null
          usage_stats?: {
            requests_this_minute: number
            requests_today: number
            minute_limit: number
            daily_limit: number
            minute_percent: number
            daily_percent: number
          } | null
        }

        // Converter resposta para formato AIResponse
        const aiResponse: AIResponse = {
          answer: data.answer || 'Resposta não disponível. A IA não conseguiu gerar uma resposta.',
          tokens_used: 0, // Backend não retorna tokens no response atual
          provider: 'gemini',
          cached: false,
        }

        // Adicionar resposta da IA aos messages
        const assistantMessage: AIMessage = {
          id: uuidv4(),
          type: 'assistant',
          content: aiResponse.answer,
          tokens_used: aiResponse.tokens_used,
          provider: aiResponse.provider,
          cached: aiResponse.cached,
          timestamp: Date.now(),
        }
        setMessages((prev) => [...prev, assistantMessage])

        // Retornar também informações de uso para o componente
        const responseWithUsage = {
          ...aiResponse,
          usage_alert: data.usage_alert || null,
          usage_stats: data.usage_stats || null
        }

        options?.onSuccess?.(responseWithUsage as any)
        setIsLoading(false)

        return responseWithUsage as any
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Erro desconhecido ao chamar IA'
        setError(errorMessage)
        options?.onError?.(errorMessage)
        setIsLoading(false)
        return null
      }
    },
    [headers, options]
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    messages,
    isLoading,
    error,
    askQuestion,
    clearMessages,
    clearError,
  }
}
